import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { salesApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

type Tab = 'promotions' | 'coupons' | 'validate';

/** FR-SALES-05 — Promotions + coupons */
export function PromotionsPage() {
  const [tab, setTab] = useState<Tab>('promotions');

  const TABS: Array<{ key: Tab; label: string }> = [
    { key: 'promotions', label: 'Promotions' },
    { key: 'coupons',    label: 'Coupons' },
    { key: 'validate',   label: 'Test validation' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Commerce"
        title="Promotions & coupons"
        subtitle="Discount rules, coupon codes, and cart-level validation testing."
      />

      <div className="card p-1.5 mb-6 inline-flex gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 text-sm font-semibold rounded-pill transition-all ${
              tab === t.key
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'promotions' && <PromotionsTab />}
      {tab === 'coupons' && <CouponsTab />}
      {tab === 'validate' && <ValidateTab />}
    </div>
  );
}

function PromotionsTab() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<any | null>(null);

  const q = useQuery({
    queryKey: ['promotions'],
    queryFn: () => salesApi.listPromotions({ limit: 200 }),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, payload }: any) => salesApi.updatePromotion(id, payload),
    onSuccess: () => { toast.success('Promotion updated'); qc.invalidateQueries({ queryKey: ['promotions'] }); setEditing(null); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, status }: any) => salesApi.updatePromotion(id, { status }),
    onSuccess: (_d: any, v: any) => { toast.success(v.status === 'active' ? 'Promotion activated' : 'Promotion deactivated'); qc.invalidateQueries({ queryKey: ['promotions'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New promotion</button>
      </div>

      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card overflow-x-auto min-w-0">
            <table className="table">
              <thead><tr><th>Code</th><th>Name</th><th>Type</th><th className="text-right">Value</th><th>Status</th><th className="text-right w-40">Actions</th></tr></thead>
              <tbody>
                {!q.data?.length ? (
                  <tr><td colSpan={6}><EmptyState message="No promotions." /></td></tr>
                ) : q.data.map((p: any) => (
                  <tr key={p.id} onClick={() => setSelected(p.id)}
                      className={`cursor-pointer ${selected === p.id ? 'bg-sage-50' : 'hover:bg-canvas-subtle'}`}>
                    <td className="font-mono text-xs">{p.code}</td>
                    <td className="text-xs">{p.name}</td>
                    <td className="text-xs">{p.type}</td>
                    <td className="text-right font-mono text-xs">
                      {p.type === 'percentage' ? `${p.value}%` : `${fmtCurrency(parseFloat(p.value))}`}
                    </td>
                    <td><span className={`badge ${p.status === 'active' ? 'bg-sage-50 text-sage-700 ring-sage-200' : 'bg-canvas-subtle text-ink-600 ring-ink-200'} ring-1 ring-inset text-2xs`}>{p.status}</span></td>
                    <td className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button className="text-xs font-medium px-2 py-1 rounded hover:bg-canvas-subtle transition-colors text-ink-600"
                          onClick={() => setEditing(p)}>Edit</button>
                        <button
                          className={`text-xs font-medium px-2 py-1 rounded transition-colors ${p.status === 'active' ? 'text-ink-500 hover:bg-canvas-subtle' : 'text-sage-700 hover:bg-sage-50'}`}
                          disabled={toggleMut.isPending}
                          onClick={() => toggleMut.mutate({ id: p.id, status: p.status === 'active' ? 'inactive' : 'active' })}>
                          {p.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card p-4">
            {!selected ? <EmptyState message="Select a promotion." /> : (
              <PromotionDetail promotionId={selected}
                onChanged={() => qc.invalidateQueries({ queryKey: ['promotions'] })} />
            )}
          </div>
        </div>
      )}

      {showCreate && <CreatePromoModal onClose={() => setShowCreate(false)}
        onCreated={() => { qc.invalidateQueries({ queryKey: ['promotions'] }); setShowCreate(false); }} />}
      {editing && <EditPromoModal promotion={editing}
        onClose={() => setEditing(null)}
        onSaved={(p) => updateMut.mutate({ id: editing.id, payload: p })}
        loading={updateMut.isPending} />}
    </div>
  );
}

function PromotionDetail({ promotionId, onChanged }: { promotionId: string; onChanged: () => void }) {
  const q = useQuery({
    queryKey: ['promotion', promotionId],
    queryFn: () => salesApi.getPromotion(promotionId),
  });

  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} />;
  const p = q.data;

  return (
    <div>
      <div className="font-mono text-xs text-slate-500">{p.code}</div>
      <div className="font-semibold text-lg">{p.name}</div>
      {p.description && <div className="text-sm text-slate-600 mb-2">{p.description}</div>}

      <div className="grid grid-cols-2 gap-2 text-sm mb-3">
        <div><span className="text-slate-500 text-xs">Type:</span> {p.type}</div>
        <div><span className="text-slate-500 text-xs">Value:</span> {p.type === 'percentage' ? `${p.value}%` : `${fmtCurrency(parseFloat(p.value))}`}</div>
        {p.min_purchase && <div><span className="text-slate-500 text-xs">Min purchase:</span> ${parseFloat(p.min_purchase).toFixed(2)}</div>}
        <div><span className="text-slate-500 text-xs">Stackable:</span> {p.stackable ? 'Yes' : 'No'}</div>
        <div className="col-span-2"><span className="text-slate-500 text-xs">Valid:</span>
          {' '}{new Date(p.starts_at).toLocaleDateString()} → {new Date(p.ends_at).toLocaleDateString()}
        </div>
        {p.customer_segments?.length > 0 && (
          <div className="col-span-2">
            <span className="text-slate-500 text-xs">Restricted to:</span>
            {' '}{p.customer_segments.join(', ')}
          </div>
        )}
        <div><span className="text-slate-500 text-xs">Usage:</span>
          {' '}{p.usage_count}{p.usage_limit && ` / ${p.usage_limit}`}
        </div>
      </div>

      <h3 className="font-semibold text-sm mt-4 mb-1">Coupons ({p.coupons?.length ?? 0})</h3>
      {!p.coupons?.length ? <div className="text-xs text-slate-400">No coupons issued for this promotion.</div> : (
        <div className="max-h-64 overflow-y-auto">
          <table className="table text-xs">
            <thead><tr><th>Code</th><th>Used</th><th>Status</th></tr></thead>
            <tbody>
              {p.coupons.slice(0, 20).map((c: any) => (
                <tr key={c.id}>
                  <td className="font-mono">{c.code}</td>
                  <td className="text-right">{c.usage_count}{c.usage_limit && `/${c.usage_limit}`}</td>
                  <td>{c.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {p.coupons.length > 20 && <div className="text-xs text-slate-400 mt-1">…and {p.coupons.length - 20} more</div>}
        </div>
      )}
    </div>
  );
}

function CreatePromoModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const in30 = (() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); })();

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'percentage' | 'fixed_amount' | 'bogo' | 'tiered'>('percentage');
  const [value, setValue] = useState(10);
  const [minPurchase, setMinPurchase] = useState(0);
  const [startsAt, setStartsAt] = useState(today);
  const [endsAt, setEndsAt] = useState(in30);
  const [generateCoupons, setGenerateCoupons] = useState(0);
  const [couponPrefix, setCouponPrefix] = useState('');
  const [usageLimit, setUsageLimit] = useState<string>('');

  const mut = useMutation({
    mutationFn: () => salesApi.createPromotion({
      code: code.toUpperCase(), name, description: description || undefined,
      type, value,
      minPurchase: minPurchase > 0 ? minPurchase : undefined,
      startsAt: new Date(startsAt + 'T00:00:00').toISOString(),
      endsAt: new Date(endsAt + 'T23:59:59').toISOString(),
      usageLimit: usageLimit ? parseInt(usageLimit) : undefined,
      generateCoupons,
      couponPrefix: couponPrefix || undefined,
    }),
    onSuccess: (d) => {
      toast.success(
        d.couponsIssued > 0
          ? `Created ${d.code} with ${d.couponsIssued} coupon${d.couponsIssued === 1 ? '' : 's'}`
          : `Created ${d.code}`,
      );
      onCreated();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">Sales · Promotions</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New promotion</h2>
      </div>

      <div className="px-6 py-5 grid grid-cols-2 gap-4 overflow-y-auto flex-1 min-h-0">
        <div>
          <label className="label">Code <span className="text-danger">*</span></label>
          <input className="input font-mono uppercase" value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="SUMMER20" />
        </div>
        <div>
          <label className="label">Type</label>
          <select className="input" value={type} onChange={(e) => setType(e.target.value as any)}>
            <option value="percentage">Percentage off</option>
            <option value="fixed_amount">Fixed amount off</option>
            <option value="bogo">BOGO (fixed v1)</option>
            <option value="tiered">Tiered (fixed v1)</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">Name <span className="text-danger">*</span></label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Summer 20% off" />
        </div>
        <div className="col-span-2">
          <label className="label">Description <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div>
          <label className="label">{type === 'percentage' ? 'Percent (0–100)' : 'Amount'} <span className="text-danger">*</span></label>
          <input type="number" min={0} max={type === 'percentage' ? 100 : undefined} step={0.01}
            className="input tabular-nums" value={value}
            onChange={(e) => setValue(parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <label className="label">Min purchase <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input type="number" min={0} step={1} className="input tabular-nums" value={minPurchase}
            onChange={(e) => setMinPurchase(parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <label className="label">Starts</label>
          <input type="date" className="input" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
        </div>
        <div>
          <label className="label">Ends <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input type="date" className="input" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
        </div>
        <div>
          <label className="label">Total usage limit <span className="font-normal text-ink-400 normal-case tracking-normal">(blank = unlimited)</span></label>
          <input type="number" min={0} className="input tabular-nums" value={usageLimit}
            onChange={(e) => setUsageLimit(e.target.value)} placeholder="unlimited" />
        </div>
        <div>
          <label className="label">Generate N coupons</label>
          <input type="number" min={0} max={10000} className="input tabular-nums" value={generateCoupons}
            onChange={(e) => setGenerateCoupons(parseInt(e.target.value) || 0)} />
        </div>
        {generateCoupons > 0 && (
          <div className="col-span-2">
            <label className="label">Coupon prefix <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
            <input className="input font-mono uppercase" value={couponPrefix}
              onChange={(e) => setCouponPrefix(e.target.value.toUpperCase())}
              placeholder="SUMMER" />
            <div className="text-2xs text-ink-500 mt-1">
              Each coupon gets prefix + 8 random chars.
            </div>
          </div>
        )}
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!code || !name || value <= 0 || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create promotion'}
        </button>
      </div>
    </Modal>
  );
}

function CouponsTab() {
  const [statusFilter, setStatusFilter] = useState('');
  const q = useQuery({
    queryKey: ['coupons', statusFilter],
    queryFn: () => salesApi.listCoupons({ status: statusFilter || undefined, limit: 200 }),
  });

  return (
    <div>
      <div className="flex gap-2 mb-3 items-center">
        <select className="input max-w-xs" value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="exhausted">Exhausted</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} /> : (
        <div className="card overflow-x-auto">
          <table className="table">
            <thead><tr><th>Code</th><th>Promotion</th><th>Type</th><th>Used</th><th>Expires</th><th>Customer</th><th>Status</th></tr></thead>
            <tbody>
              {!q.data?.length ? (
                <tr><td colSpan={7}><EmptyState message="No coupons." /></td></tr>
              ) : q.data.map((c: any) => (
                <tr key={c.id}>
                  <td className="font-mono text-xs">{c.code}</td>
                  <td className="text-xs">{c.promo_code}</td>
                  <td className="text-xs">{c.promo_type}</td>
                  <td className="text-xs text-right">{c.usage_count}{c.usage_limit && `/${c.usage_limit}`}</td>
                  <td className="text-xs">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}</td>
                  <td className="text-xs">{c.customer_name ?? '—'}</td>
                  <td><span className="badge bg-canvas-subtle text-ink-700 ring-ink-200 text-2xs">{c.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ValidateTab() {
  const [code, setCode] = useState('');
  const [subtotal, setSubtotal] = useState(100);
  const [result, setResult] = useState<any>(null);

  const mut = useMutation({
    mutationFn: () => salesApi.validateCoupon({ code: code.toUpperCase(), subtotal }),
    onSuccess: (d) => setResult(d),
    onError: (e) => { toast.error(getErrorMessage(e)); setResult(null); },
  });

  return (
    <div className="card p-4 max-w-lg">
      <h3 className="font-semibold mb-3">Test a coupon code</h3>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="label">Coupon code</label>
          <input className="input font-mono uppercase" value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())} />
        </div>
        <div>
          <label className="label">Cart subtotal</label>
          <input type="number" min={0} step={0.01} className="input" value={subtotal}
            onChange={(e) => setSubtotal(parseFloat(e.target.value) || 0)} />
        </div>
      </div>
      <button className="btn-primary w-full mb-3" disabled={!code || mut.isPending} onClick={() => mut.mutate()}>
        {mut.isPending ? 'Validating…' : 'Validate'}
      </button>

      {result && (
        <div className={`p-3 rounded border ${result.valid
          ? 'bg-emerald-50 border-emerald-200'
          : 'bg-red-50 border-red-200'
        }`}>
          {result.valid ? (
            <div className="text-sm">
              <div className="font-semibold text-emerald-700">✓ Valid</div>
              <div className="mt-1">Promotion: <strong>{result.promotion.name}</strong> ({result.promotion.code})</div>
              <div>Type: {result.promotion.type} / Value: {result.promotion.value}</div>
              <div className="mt-1 text-lg font-bold">Discount: ${result.discountAmount.toFixed(2)}</div>
              <div className="text-xs text-slate-500">Applied to subtotal ${result.appliedTo.toFixed(2)}</div>
            </div>
          ) : (
            <div className="text-sm">
              <div className="font-semibold text-red-700">✗ Invalid</div>
              <div className="mt-1">{result.reason}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EditPromoModal({ promotion, onClose, onSaved, loading }: {
  promotion: any; onClose: () => void; onSaved: (p: any) => void; loading: boolean;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [name, setName] = useState(promotion.name ?? '');
  const [description, setDescription] = useState(promotion.description ?? '');
  const [startsAt, setStartsAt] = useState(promotion.starts_at?.slice(0, 10) ?? today);
  const [endsAt, setEndsAt] = useState(promotion.ends_at?.slice(0, 10) ?? '');
  const [usageLimit, setUsageLimit] = useState(promotion.usage_limit ? String(promotion.usage_limit) : '');

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Commerce · Promotions</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Edit promotion</h2>
        <p className="text-sm text-ink-500 mt-1 font-mono">{promotion.code}</p>
      </div>
      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Name <span className="text-danger">*</span></label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="label">Description <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Starts</label>
            <input type="date" className="input" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
          </div>
          <div>
            <label className="label">Ends</label>
            <input type="date" className="input" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="label">Total usage limit <span className="font-normal text-ink-400 normal-case tracking-normal">(blank = unlimited)</span></label>
          <input type="number" min={0} className="input tabular-nums" value={usageLimit} onChange={(e) => setUsageLimit(e.target.value)} placeholder="unlimited" />
        </div>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={loading || !name}
          onClick={() => onSaved({ name, description: description || undefined, starts_at: startsAt, ends_at: endsAt || undefined, usage_limit: usageLimit ? parseInt(usageLimit) : null })}>
          {loading ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </Modal>
  );
}
